<template>
  <VisionButton
    background-colour="gradient"
    size="large"
    :class="$b()"
    :disabled="
      (soldOut &&
      !(
        !props.customisedProduct.lensTechSelected &&
        props.requiresLensTechSelection
      )) || props.customisedProduct.allAvailableLensTechsOutOfStock
    "
    :square="true"
    @click="addToCart"
  >
    <template #content>
      <span
        ref="addToBagButton"
        :class="$b('button-content')"
      >
        <span :class="$b('button-text')">
          {{ buttonText }}
          <span
            v-if="suffixText && !cartUpdating"
            :class="$b('suffix')"
          >
            {{ suffixText }}
          </span>
        </span>
        <ProductPrice
          :is-minimum-price="customisedProduct.showMinimumPrice"
          :price="price"
          :colour="'white'"
          :class="$b('price')"
        />
      </span>
    </template>
  </VisionButton>
</template>

<script setup>
import CustomisedProduct from '~/models/CustomisedProduct'
import {
  buildCartItems,
  buildQtyChangePayload,
  getUpdateProductQuantityPayload,
} from '~/assets/js/helpers/shopify'

const alertStore = useAlertStore()

const cartUpdating = ref(false)
const shopifyStore = useShopifyStore()
const promoStore = usePromoStore()

const { $track, $sentry, $product } = useNuxtApp()

defineOptions({
  name: 'AddToBag',
})

const emits = defineEmits([
  'lens-tech-not-selected',
  'toggle-promo-selector',
  'set-primary-sku-added-to-cart',
  'scrolled-past-view',
])

const props = defineProps({
  customisedProduct: { type: CustomisedProduct, required: true },
  price: { type: [Number, null], required: true },
  trackingLocation: { type: String, required: true },
  requiresLensTechSelection: { type: Boolean, default: true },
  activePartType: { type: String, default: '' },
  isStickyButton: { type: Boolean, default: false },
})

const ticker = ref(1)
const tickerInterval = ref()
const observer = ref()
const addToBagButton = ref()

onMounted(() => {
  tickerInterval.value = setInterval(() => ticker.value++, 1000)

  /**
   * Only emit the event once the user has scrolled past the add to bag button
   * and the button is no longer in view.
   */
  if (addToBagButton.value && !soldOut.value && !props.isStickyButton) {
    observer.value = new IntersectionObserver(([entry]) => {
      if(!entry.isIntersecting && window.scrollY >= entry.boundingClientRect.top) {
        emits('scrolled-past-view', true)
      } else {
        emits('scrolled-past-view', false)
      }
    })

    observer.value.observe(addToBagButton.value)
  }
})

onBeforeUnmount(() => clearInterval(tickerInterval.value))

const soldOut = computed(() => {
  if (props.customisedProduct.isLimitedEdition) {
    return (
      ticker.value > 0 &&
      !props.customisedProduct.isInStock &&
      !props.customisedProduct.preorderPartSelected
    )
  }

  return ticker.value > 0 && !props.customisedProduct.isInStock
})

const giftDiscounts = computed(() => {
  return shopifyStore.getDiscountsFromProduct(props.customisedProduct)
})

const isOnPreorder = computed(() => {
  if (props.customisedProduct.isLimitedEdition) {
    return props.customisedProduct.preorderPartSelected
  }

  return (
    props.customisedProduct.isInStock &&
    props.customisedProduct.preorderPartSelected
  )
})

const buttonText = computed(() => {
  if (cartUpdating.value) return 'Adding to Bag...'
  if (
    isOnPreorder.value &&
    !props.customisedProduct.allPreorderPartsAsDelayedDespatch
  )
    return 'Pre-order'
  else if (
    (soldOut.value &&
    !(
      !props.customisedProduct.lensTechSelected &&
      props.requiresLensTechSelection
    )) || props.customisedProduct.allAvailableLensTechsOutOfStock
  )
    return 'Sold out'
  else return 'Add to Bag'
})

const suffixText = computed(() => {
  if (qualifiesForFreeGift.value) {
    // If any discount for the customisedProduct has tagText set
    // Show this. If not and product is free, show Free Gift
    if (
      giftDiscounts.value.groupedDiscounts?.some(
        (discount) => discount.discountSummary.tagText
      )
    ) {
      return giftDiscounts.value.groupedDiscounts?.find(
        (discount) => discount.discountSummary.tagText
      ).discountSummary.tagText
    }

    return '+ Free Gift'
  }

  if (
    props.customisedProduct.product?.pdpConfigWithDefaults?.ctaSuffix &&
    !promoStore?.tagText
  ) {
    return props.customisedProduct.product?.pdpConfigWithDefaults?.ctaSuffix
  }

  return ''
})

const qualifiesForFreeGift = computed(() => {
  return (
    giftDiscounts.value.groupedDiscounts?.length &&
    giftDiscounts.value.anyDiscountsAreFreeAndOverThreshold &&
    giftDiscounts.value.anyDiscountThresholdPassed
  )
})

async function addGift() {
  const gift = await $product.getCustomisedProduct(
    giftDiscounts.value.groupedDiscounts[0].customerGets[0].options[0].baseSku,
    {
      sku: giftDiscounts.value.groupedDiscounts[0].customerGets[0].options[0]
        .sku,
    }
  )

  const giftCartItems = buildCartItems(gift)

  await shopifyStore.addCartItems(giftCartItems)
}

async function addToCart() {
  if (cartUpdating.value) return

  shopifyStore.toggleCart(true)

  cartUpdating.value = true

  let cartAddResponse
  let cartUpdateResponse

  if (props.customisedProduct.allowLensTechFiltering) {
    if (
      ['custom', 'lenses'].includes(
        props.customisedProduct?.product?.pageMode
      ) ||
      (props.customisedProduct?.product?.pageMode === 'parts' &&
        props.activePartType === 'lenses')
    ) {
      if (
        !props.customisedProduct.lensTechSelected &&
        props.requiresLensTechSelection
      ) {
        emits('lens-tech-not-selected', true)
        cartUpdating.value = false
        shopifyStore.toggleCart(false)

        return
      }
    }
  }

  try {
    const canAddSecondaryBundle =
      props.customisedProduct.secondarySKU &&
      props.customisedProduct.conversionKitEnabled &&
      !props.customisedProduct.isLimitedEdition &&
      !props.customisedProduct.isPrebuiltVariant

    /**
     * First check if exact product(s) are already in cart.
     *
     * If they are the call the update function to change the quantity instead of adding
     * a new cart item.
     */
    const existingPrimaryProductBundlesInCart =
      shopifyStore.existingBundlesInCartFromSku(props.customisedProduct.sku)

    const existingSecondaryProductBundlesInCart =
      shopifyStore.existingBundlesInCartFromSku(
        props.customisedProduct.secondarySKU
      )

    const allExistingBundlesInCart = [
      ...existingPrimaryProductBundlesInCart,
      ...existingSecondaryProductBundlesInCart,
    ]

    if (allExistingBundlesInCart.length) {
      const updatePayload = getUpdateProductQuantityPayload(
        allExistingBundlesInCart,
        1
      )
      cartUpdateResponse = await shopifyStore.updateCartItems(updatePayload)
      shopifyStore.toggleCart(true)
    }

    const builtCartItems = []

    /**
     * If there was no existing matching primary product (customisedProduct.sku) then
     * the quantity for that will not have been updated as it doesn't exist as a bundled
     * line item. Therefore we need to prep a new cart item to send to shopify to add it
     * to the cart.
     */
    if (!existingPrimaryProductBundlesInCart.length) {
      builtCartItems.push(...buildCartItems(props.customisedProduct))
    }

    /**
     * Similar to the above if there is no secondary product (customisedProduct.secondarySKU)
     * we need to prep a new cart item to send to shopify to add it to the cart.
     */
    if (
      canAddSecondaryBundle &&
      !existingSecondaryProductBundlesInCart.length
    ) {
      const secondaryProduct = await $product.getCustomisedProduct(
        props.customisedProduct.secondaryProductBaseSKU,
        { sku: props.customisedProduct.secondarySKU }
      )

      builtCartItems.push(...buildCartItems(secondaryProduct))
    }

    /**
     * If there are cart items to add then we should send these to Shopify to
     * add to the cart.
     */
    if (builtCartItems.length) {
      cartAddResponse = await shopifyStore.addCartItems(builtCartItems)
      emits('set-primary-sku-added-to-cart', {
        sku: props.customisedProduct.sku,
        baseSku: props.customisedProduct.baseSKU,
      })
    }

    if (buildCartItems.length && !existingPrimaryProductBundlesInCart.length) {
      /**
       * We only want to add the free gift if there wasn't already a primary product
       * in the cart to avoid added a second gift product for the same line item.
       */

      // Build and add gift automatically if free and there is only one option
      if (
        qualifiesForFreeGift.value &&
        giftDiscounts.value.allDiscountOptions?.length === 1
      ) {
        await addGift()
      } else if (
        giftDiscounts.value.anyDiscountThresholdPassed &&
        giftDiscounts.value.allDiscountOptions?.length > 1
      ) {
        emits('toggle-promo-selector', true)
      }
    } else if (
      giftDiscounts.value.anyDiscountThresholdPassed &&
      giftDiscounts.value.allDiscountOptions?.length > 1
    ) {
      const freeGiftForParentSku = Object.values(
        shopifyStore.bundledCartItems
      ).find((bundleItem) => {
        return (
          bundleItem.summary.bundleParentSku === props.customisedProduct.sku
        )
      })

      if (!freeGiftForParentSku) return

      /**
       * Increment the free gift already in cart associated with the product that has
       * just been added as the same sku has been added again to cart resulting
       * in an increase in parent sku quantity instead of a new line item meaning the
       * free gift should also be incremented.
       */
      await shopifyStore.updateCartItems(
        buildQtyChangePayload(
          freeGiftForParentSku,
          freeGiftForParentSku.summary.bundleQuantity + 1
        )
      )

      await $track.giftSelector.incrementGift(freeGiftForParentSku)
    }
  } catch (error) {
    console.log(error)
    $sentry.captureException(error)
    shopifyStore.toggleCart(false)

    alertStore.ADD_ERROR_MESSAGE({
      body: 'There was an error adding the product to your bag. Please try again.',
    })
  }

  if (cartAddResponse || cartUpdateResponse) {
    await $track.product.add(props.customisedProduct, {
      location: props.trackingLocation,
      quantity: 1
    })
  }

  cartUpdating.value = false
}
</script>

<style lang="postcss">
.AddToBag {
  @apply w-full;
}

.AddToBag__button-content {
  @apply w-full flex items-baseline justify-between text-[18px];

  color: var(--add-to-bag-text-colour);
}

.AddToBag__button-text {
  @apply text-left;
}

.AddToBag__price.ProductPrice {
  @apply text-[18px] text-white font-semibold;
}

.AddToBag__suffix {
  @apply font-thin inline-block opacity-50;
}
</style>
